/**
 * Shared Button atom — Agency Portal v2 Slice 4 (§4.0).
 *
 * One source of truth for primary / secondary / ghost / danger buttons
 * across the dashboard. Agency pages used `background: C.text` (black on
 * cream, muted on dark) for primary actions which drifted from the rest
 * of the dashboard where the primary action is lime on ink. This atom
 * pins the shipped lime treatment and migrates the drift behind one
 * component.
 *
 * Variants (spec §4.0):
 *   primary   — lime bg, ink text. Geist 600 13 px, h 34, radius 2.
 *   secondary — surface bg + 1 px border, text colour. Geist 500 13 px.
 *   ghost     — transparent, text colour, underline on hover.
 *   danger    — transparent, 1 px coral border, coral text.
 *
 * `loading` keeps the label width stable (the spinner replaces the icon,
 * never collapses the glyph box), sets `aria-busy="true"` and disables
 * the button so a double submit cannot fire.
 *
 * No new dependency: the spinner is a plain inline SVG. Hover / active
 * tints are inline (no :hover CSS so this works inside the `useC()`
 * token model without a <style> tag).
 */

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

/** Lucide-icon-compatible shape. The upstream type is a
 *  `ForwardRefExoticComponent` that doesn't line up with a plain
 *  `ComponentType` under `exactOptionalPropertyTypes`, so we accept
 *  the broader callable shape and let React figure it out. */
export type ButtonIcon =
  | ((props: { size?: number; color?: string }) => JSX.Element | null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | React.ComponentType<any>;

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly loading?: boolean;
  readonly icon?: ButtonIcon;
  readonly fullWidth?: boolean;
  readonly children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon: Icon,
    fullWidth = false,
    children,
    disabled,
    type = 'button',
    style,
    ...rest
  },
  ref,
) {
  const C = useC();
  const isDisabled = disabled === true || loading;

  const height = size === 'sm' ? 28 : 34;
  const padX = size === 'sm' ? 10 : 16;
  const fontSize = size === 'sm' ? 12 : 13;
  const iconSize = size === 'sm' ? 12 : 13;

  // Base style — padding/height/radius consistent across variants.
  const base: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height,
    paddingLeft: padX,
    paddingRight: padX,
    borderRadius: 2,
    fontFamily: sansFont,
    fontSize,
    fontWeight: variant === 'primary' ? 600 : 500,
    letterSpacing: -0.1,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    width: fullWidth ? '100%' : undefined,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.5 : 1,
    transition: 'background-color 120ms ease, border-color 120ms ease',
    userSelect: 'none',
  };

  let variantStyle: React.CSSProperties;
  let iconColor = C.text;
  switch (variant) {
    case 'primary':
      variantStyle = {
        background: C.lime,
        color: C.ink,
        border: '1px solid transparent',
      };
      iconColor = C.ink;
      break;
    case 'secondary':
      variantStyle = {
        background: C.surface,
        color: C.text,
        border: `1px solid ${C.border}`,
      };
      iconColor = C.muted;
      break;
    case 'ghost':
      variantStyle = {
        background: 'transparent',
        color: C.text,
        border: '1px solid transparent',
      };
      iconColor = C.muted;
      break;
    case 'danger':
      variantStyle = {
        background: 'transparent',
        color: C.coralFg,
        border: `1px solid ${C.coral}`,
      };
      iconColor = C.coralFg;
      break;
  }

  const merged: React.CSSProperties = { ...base, ...variantStyle, ...style };

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      style={merged}
      {...rest}
    >
      {loading ? (
        <Spinner size={iconSize} color={iconColor} />
      ) : Icon ? (
        <Icon size={iconSize} color={iconColor} />
      ) : null}
      <span>{children}</span>
    </button>
  );
});

function Spinner({ size, color }: { size: number; color: string }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      style={{ animation: 'sill-btn-spin 0.9s linear infinite' }}
    >
      <circle cx="8" cy="8" r="6" fill="none" stroke={color} strokeOpacity="0.25" strokeWidth="2" />
      <path
        d="M14 8a6 6 0 0 1-6 6"
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <style>{`@keyframes sill-btn-spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}
